import { useCallback, useEffect, useRef, useState } from 'react';
import {
  LayoutChangeEvent,
  NativeScrollEvent,
  NativeSyntheticEvent,
} from 'react-native';
import {
  ListNavigationOptions,
  ListNavigationResult,
} from './useListNavigation.types';
import { useArrowNavigation, useHorizontalDrag } from '../../index';
import { DragOptions } from '../useHorizontalDrag/useHorizontalDrag.types';
import { WithScrollToOffset } from '../useArrowNavigation/useArrowNavigation.types';

export default function useListNavigation<T extends WithScrollToOffset>(
  options: ListNavigationOptions<T>,
): ListNavigationResult {
  const {
    listRef,
    containerRef,
    containerWidth: initialContainerWidth = 0,
    contentWidth: initialContentWidth = 0,
    itemWidth = 100,
    itemsCount = 0,
    showArrows = true,
    enableDragging = true,
    enableArrows = true,
    dragOptions = {},
  } = options;

  const [containerWidth, setContainerWidth] = useState(initialContainerWidth);
  const [contentWidth, setContentWidth] = useState(initialContentWidth);
  const initialCheckDoneRef = useRef(false);
  const isDraggingRef = useRef(false);
  const scrollOffsetRef = useRef(0);
  const contentWidthRef = useRef(initialContentWidth);

  const arrowNavigation = useArrowNavigation(
    listRef,
    scrollOffsetRef,
    contentWidthRef,
    {
      containerWidth,
      contentWidth,
      itemWidth,
      showArrows: enableArrows && showArrows,
      itemsCount,
    },
  );

  const { speedMultiplier, friction, momentumMultiplier, velocityThreshold } =
    dragOptions;

  const horizontalDragOptions: DragOptions = {
    speedMultiplier,
    friction,
    momentumMultiplier,
    velocityThreshold,
    onDragStart: () => {
      if (enableDragging) {
        isDraggingRef.current = true;
      }
    },
    onDragEnd: () => {
      if (enableDragging) {
        isDraggingRef.current = false;
        if (contentWidthRef.current !== contentWidth) {
          setContentWidth(contentWidthRef.current);
        }
        if (enableArrows) {
          arrowNavigation.updateArrowVisibility?.();
        }
      }
    },
  };

  useHorizontalDrag(
    containerRef,
    listRef,
    scrollOffsetRef,
    horizontalDragOptions,
  );

  useEffect(() => {
    if (
      containerWidth > 0 &&
      contentWidth > 0 &&
      !initialCheckDoneRef.current &&
      enableArrows
    ) {
      arrowNavigation.updateArrowVisibility?.();
      initialCheckDoneRef.current = true;
    }
  }, [containerWidth, contentWidth, arrowNavigation, enableArrows]);

  const handleLayout = useCallback(
    (event: LayoutChangeEvent) => {
      if (enableArrows) {
        arrowNavigation.handleLayout?.(event);
      }
      setContainerWidth(event.nativeEvent.layout.width);
    },
    [arrowNavigation, enableArrows],
  );

  const handleScroll = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      const { contentOffset, contentSize } = event.nativeEvent;
      scrollOffsetRef.current = contentOffset.x;
      contentWidthRef.current = contentSize.width;

      if (contentSize.width !== contentWidth && !isDraggingRef.current) {
        setContentWidth(contentSize.width);
        if (enableArrows) {
          arrowNavigation.updateArrowVisibility?.();
        }
      } else if (!isDraggingRef.current && enableArrows) {
        arrowNavigation.updateArrowVisibility?.();
      }
    },
    [contentWidth, arrowNavigation, enableArrows],
  );

  const result: ListNavigationResult = {
    handleScroll,
    handleLayout,
    showLeftArrow: enableArrows ? arrowNavigation.showLeftArrow : false,
    showRightArrow: enableArrows ? arrowNavigation.showRightArrow : false,
    isLeftArrowHovered: enableArrows
      ? arrowNavigation.isLeftArrowHovered
      : false,
    isRightArrowHovered: enableArrows
      ? arrowNavigation.isRightArrowHovered
      : false,
    containerWidth,
    contentWidth,
    scrollOffsetRef,
    isDragging: isDraggingRef.current,
  };

  if (enableArrows) {
    result.handleContentSizeChange = arrowNavigation.handleContentSizeChange;
    result.handleArrowForward = arrowNavigation.handleArrowForward;
    result.handleArrowBack = arrowNavigation.handleArrowBack;
    result.handleLeftArrowMouseEnter =
      arrowNavigation.handleLeftArrowMouseEnter;
    result.handleLeftArrowMouseLeave =
      arrowNavigation.handleLeftArrowMouseLeave;
    result.handleRightArrowMouseEnter =
      arrowNavigation.handleRightArrowMouseEnter;
    result.handleRightArrowMouseLeave =
      arrowNavigation.handleRightArrowMouseLeave;
  }

  return result;
}
